// src/contexts/CustomerAuthContext.jsx - FIXED version to prevent auth conflicts
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../utils/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { getCurrentCustomer } from '../api/customerAuthService';
import { getSalonConfig } from '../api/salonConfigService';
// #Revised UI_v2 — CE1/CE2: brand colour + typography injection
import { useBrandInjector } from '../hooks/useBrandInjector';
import { 
  requestNotificationPermission, 
  saveCustomerFCMToken, 
  onForegroundMessage,
  isNotificationSupported 
} from '../services/fcmService';

const CustomerAuthContext = createContext();

export function useCustomerAuth() {
  return useContext(CustomerAuthContext);
}

export function CustomerAuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salonConfig, setSalonConfig] = useState(null);
  // Initialize salonId as null - it will be set from URL in determineSalonId()
  const [salonId, setSalonId] = useState(null);

  // #Revised UI_v2 — CE1/CE2: inject brand CSS variables whenever salonConfig loads
  useBrandInjector(salonConfig);
  
  const location = useLocation();
  
  // Helper function to determine if current path is customer-related
  const isCustomerRelatedPath = (pathname) => {
    return pathname.includes('/customer/') || 
           pathname.includes('/salon/') ||
           pathname.includes('/verify-email') ||
           pathname.includes('/unsubscribe');
  };
  
  // Function to update the salon ID (to be called from components)
  const updateSalonId = (id) => {
    const normId = id?.toLowerCase();
    if (normId && normId !== 'bespokebeauty' && normId !== ':salonid') {
      console.log('Updating customer salon ID to:', normId);
      localStorage.setItem('customerSalonId', normId);
      setSalonId(normId);
    }
  };
  
  // FCM token management function for customers
  const requestAndSaveCustomerFCMToken = async (customer) => {
    try {
      if (!isNotificationSupported()) {
        console.log('Push notifications not supported in this environment');
        return;
      }

      // Only request if customer has opted into push notifications
      if (customer.preferences?.notifications?.push !== false) {
        const token = await requestNotificationPermission();
        if (token) {
          const customerId = `${customer.email.toLowerCase()}_${customer.salonId}`;
          await saveCustomerFCMToken(customerId, token);
          console.log('FCM token saved for customer:', customerId);
        }
      }
    } catch (error) {
      console.error('Error requesting customer FCM token:', error);
    }
  };

  // Foreground message listener setup
  const setupForegroundMessageListener = () => {
    return onForegroundMessage((payload) => {
      console.log('Received foreground message in customer context:', payload);
    });
  };
  
  // ENHANCED: Determine salon ID with URL taking priority over cached values
  const determineSalonId = () => {
    // Priority 1: Extract from current URL path (URL should always take precedence)
    const pathParts = location.pathname.split('/').filter(part => part !== '');
    console.log('Path parts for customer:', pathParts);

    let extractedSalonId = null;

    // Handle different URL patterns
    if (pathParts.includes('BespokeBeauty')) {
      // Pattern: /BespokeBeauty/:salonId/customer/...
      const bespokeIndex = pathParts.indexOf('BespokeBeauty');
      if (bespokeIndex >= 0 && pathParts.length > bespokeIndex + 1) {
        extractedSalonId = pathParts[bespokeIndex + 1]?.toLowerCase();
      }
    } else if (pathParts.includes('salon')) {
      // Legacy pattern: /salon/:salonId/...
      const salonIndex = pathParts.indexOf('salon');
      if (salonIndex >= 0 && pathParts.length > salonIndex + 1) {
        extractedSalonId = pathParts[salonIndex + 1]?.toLowerCase();
      }
    }

    // Validate that extracted ID is not a route keyword
    const routeKeywords = [
      'login', 'verify-email', 'dashboard', 'customer', 'admin',
      'home', 'book', 'appointments', 'profile', 'loyalty',
      'services', 'booking', 'bespokebeauty'
    ];

    if (extractedSalonId && !routeKeywords.includes(extractedSalonId)) {
      console.log('Customer salon ID extracted from path:', extractedSalonId);
      // Update state and localStorage to match URL (always normalised to lowercase)
      if (extractedSalonId !== salonId) {
        setSalonId(extractedSalonId);
        localStorage.setItem('customerSalonId', extractedSalonId);
      }
      return extractedSalonId;
    }

    // Priority 2: Fall back to state if URL doesn't have valid salon ID
    if (salonId && salonId !== 'BespokeBeauty' && salonId !== ':salonId') {
      console.log('Using salon ID from state (URL had no valid ID):', salonId);
      return salonId;
    }

    // Priority 3: Fall back to localStorage
    const storedSalonId = localStorage.getItem('customerSalonId');
    if (storedSalonId && storedSalonId !== 'BespokeBeauty' && storedSalonId !== ':salonId') {
      console.log('Customer salon ID from localStorage (URL had no valid ID):', storedSalonId);
      setSalonId(storedSalonId);
      return storedSalonId;
    }

    console.warn('No valid customer salon ID could be determined from path:', location.pathname);
    console.warn('Path parts:', pathParts);
    console.warn('Extracted (invalid):', extractedSalonId);

    // No fallback - return null to force proper context
    return null;
  };
  
  // Fetch salon configuration
  const fetchSalonConfig = async (id) => {
    if (!id) {
      console.error('Cannot fetch salon config: No salon ID provided');
      return null;
    }
    
    try {
      console.log('Fetching customer portal salon configuration for salonId:', id);
      const config = await getSalonConfig(id);
      console.log('Salon configuration fetched for customer portal:', !!config);
      setSalonConfig(config);
      return config;
    } catch (err) {
      console.error('Error fetching salon configuration for customer:', err);
      setError('Failed to load salon configuration');
      return null;
    }
  };

  // Add explicit logout handler for Customer
  const handleCustomerLogout = async () => {
    try {
      // Set flag to indicate this is an explicit Customer logout
      sessionStorage.setItem('customer_explicit_logout', 'true');
      
      // Import and use the logout function
      const { signOutCustomer } = await import('../api/customerAuthService');
      await signOutCustomer();
      
      return true;
    } catch (error) {
      console.error('Error in Customer logout:', error);
      sessionStorage.removeItem('customer_explicit_logout');
      throw error;
    }
  };

  useEffect(() => {
    // FIXED: Only run customer auth logic if on customer-related paths
    const currentPath = location.pathname;
    const isCustomerPath = isCustomerRelatedPath(currentPath);
    
    // If not on customer path, set loading to false immediately and don't process
    if (!isCustomerPath) {
      console.log('CustomerAuthContext - Not on customer path, skipping auth logic');
      setLoading(false);
      return;
    }
    
    // Determine the salon ID to use
    const currentSalonId = determineSalonId();
    console.log('Current customer salon ID determined:', currentSalonId);
    
    // Fetch salon configuration
    if (currentSalonId) {
      fetchSalonConfig(currentSalonId);
    } else {
      console.warn('Cannot fetch salon config without valid salonId');
    }
    
    // Authentication state listener
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Customer auth state changed:', !!user, 'Current salon ID:', currentSalonId);
      
      // FIXED: Only process customer auth if on customer-related paths
      const currentPath = window.location.pathname;
      const isCustomerPath = isCustomerRelatedPath(currentPath);
      
      if (!isCustomerPath) {
        console.log('CustomerAuthContext - Not on customer path, skipping auth processing');
        setLoading(false);
        return;
      }
      
      if (user && currentSalonId) {
        try {
          // Attempt to get current customer data - pass user directly to avoid race conditions
          const customerData = await getCurrentCustomer(currentSalonId, user);
          console.log('Customer data loaded:', !!customerData);
          
          if (customerData) {
            setCurrentUser(user);
            setCurrentCustomer(customerData);
            setError(null);

            // Request FCM token for customer (non-blocking)
            requestAndSaveCustomerFCMToken(customerData);
            setupForegroundMessageListener();
          } else {
            // FIXED: Don't treat missing customer profile as an error for SuperAdmin/Salon users
            console.log('No customer profile found - this is normal for non-customer users');
            setCurrentUser(null);
            setCurrentCustomer(null);
            setError(null); // Don't set error for missing customer profile
          }
          
        } catch (err) {
          console.error('Error loading customer profile:', err);
          
          // Only set error if on customer paths
          if (isCustomerPath) {
            setError('Failed to load customer profile');
            setCurrentUser(null);
            setCurrentCustomer(null);
          }
        }
      } else {
        // Only clear state if on customer paths or during explicit logout
        const isExplicitLogout = sessionStorage.getItem('customer_explicit_logout') === 'true';
        
        if (isCustomerPath || isExplicitLogout) {
          setCurrentUser(null);
          setCurrentCustomer(null);
          setError(null);
          // Clear the explicit logout flag
          sessionStorage.removeItem('customer_explicit_logout');
        }
      }
      
      setLoading(false);
    });
    
    return unsubscribe;
  }, [salonId, location.pathname]);
  
  // Comprehensive value object
  const value = {
    currentUser,
    currentCustomer,
    loading,
    error,
    salonId,
    salonConfig,
    setError,
    fetchSalonConfig,
    updateSalonId,
    logout: handleCustomerLogout
  };
  
  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}